import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import RoomEvidencePanel from '../components/RoomEvidencePanel';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { DamageScore, EvidenceMarker, ProvenanceChip, SeverityChip, StatusChip } from '../components/evidence';
import { ProTag, useProGate } from '../components/ProUpgrade';
import { useShareReport } from '../components/ShareReportDialog';
import { useDropzone } from 'react-dropzone';
import {
  ArrowLeft,
  Upload,
  Camera,
  Loader2,
  X,
  FileDown,
  Share2,
  CheckCircle,
  Trash2,
  Eye,
  EyeOff,
  DollarSign,
  AlertTriangle,
  NotebookText,
  Clock,
  Undo2,
  Lock,
  MessageSquare,
  ShieldCheck,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { preprocessPhoto, fileToPreviewData } from '../utils/preprocessPhoto';
import { captureInspectionPhoto, isNativeApp } from '../utils/photoCapture';

import { API } from '../lib/api';
import { VIEW_OPTIONS, VIEW_LABELS_MAP, toNumber, money, formatType, damageProfile } from '../lib/damageCopy';
const ASSUMED_DEPOSIT = 1500;
const MAX_PHOTOS = 5;

const QUICK_TAGS = ['Pet Damage', 'Neglect', 'Cleaning Required', 'Missing Item', 'Stain / Spill', 'Impact Damage', 'Burn / Heat'];

const FEEDBACK_TYPES = [
  { value: 'wrong_damage', label: 'Wrong damage type' },
  { value: 'wrong_severity', label: 'Wrong severity' },
  { value: 'wrong_location', label: 'Wrong location' },
  { value: 'missed_damage', label: 'Missed damage' },
  { value: 'other', label: 'Other' },
];

const ANALYZE_STEPS = [
  { key: 'received', label: 'Received' },
  { key: 'quality_check', label: 'Quality check' },
  { key: 'ai_analyzing', label: 'AI analyzing' },
  { key: 'complete', label: 'Complete' },
];

function useIsMobileWeb() {
  const [mobile, setMobile] = useState(false);
  useEffect(() => {
    const check = () => setMobile(window.innerWidth < 768 && !isNativeApp());
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return mobile;
}

function baseCost(damage) {
  const explicit = toNumber(damage?.estimated_repair_cost_usd);
  if (explicit > 0) return explicit;
  const low = toNumber(damage?.estimated_cost_low);
  const high = toNumber(damage?.estimated_cost_high);
  if (low > 0 || high > 0) return Math.round((low + high) / 2);
  return damageProfile(damage).defaultCost;
}

export default function InspectionDetailPage() {
  const { id } = useParams();
  const [gatePro, proDialog, hasPro, openProDialog] = useProGate('PDF reports');
  const [, shareProDialog, , openShareProDialog] = useProGate('tenant share links');
  const share = useShareReport(id, { hasPro, onNeedsPro: openShareProDialog });
  const navigate = useNavigate();

  const [inspection, setInspection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeStage, setAnalyzeStage] = useState('idle'); // idle | received | quality_check | ai_analyzing | complete
  const [feedbackState, setFeedbackState] = useState({});
  const [feedbackDialog, setFeedbackDialog] = useState(null); // { idx, damage }
  const [feedbackType, setFeedbackType] = useState('wrong_damage');
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackSaving, setFeedbackSaving] = useState(false);
  const isMobileWeb = useIsMobileWeb();
  const [roomName, setRoomName] = useState('');
  const [viewLabel, setViewLabel] = useState('overview');
  const [propertyRooms, setPropertyRooms] = useState([]);
  const [roomMode, setRoomMode] = useState('list'); // 'list' picks from property rooms, 'custom' free-types
  const [photos, setPhotos] = useState([]); // [{id, base64, preview}] max MAX_PHOTOS
  const [selectedPhotoIdx, setSelectedPhotoIdx] = useState(0);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedAnalysisId, setSelectedAnalysisId] = useState(null);
  const [overlayOn, setOverlayOn] = useState(true);
  const [inspectorNotes, setInspectorNotes] = useState('');
  const [editingCostKey, setEditingCostKey] = useState(null);
  const [decisionState, setDecisionState] = useState({});
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error

  const saveTimersRef = useRef({});
  const pendingSavesRef = useRef(0);
  const notesTimerRef = useRef(null);
  const notesInitRef = useRef(null);

  const fetchInspection = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/inspections/${id}`);
      setInspection(res.data);
    } catch {
      toast.error('Inspection not found');
      navigate('/inspections');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchInspection(); }, [fetchInspection]);

  useEffect(() => {
    if (!inspection?.room_analyses?.length) return;
    if (!selectedAnalysisId) setSelectedAnalysisId(inspection.room_analyses[0].id);
  }, [inspection, selectedAnalysisId]);

  // Pull the property's room list so inspectors pick rooms instead of typing
  // them one-handed in the field. Falls back to free text if unavailable.
  useEffect(() => {
    if (!inspection?.property_id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/properties/${inspection.property_id}`);
        if (!cancelled) {
          const rooms = res.data?.rooms || [];
          setPropertyRooms(rooms);
          if (!rooms.length) setRoomMode('custom');
        }
      } catch {
        if (!cancelled) setRoomMode('custom');
      }
    })();
    return () => { cancelled = true; };
  }, [inspection?.property_id]);

  const roomOptions = useMemo(
    () => [...new Set(propertyRooms.map((r) => r.name).filter(Boolean))],
    [propertyRooms]
  );

  // Hydrate adjudication decisions persisted on the server. Local (newer,
  // possibly not-yet-saved) edits win over refetched server state.
  useEffect(() => {
    if (!inspection?.room_analyses) return;
    const serverMap = {};
    inspection.room_analyses.forEach((ra) => {
      (ra.decisions || []).forEach((dec) => {
        const damage = ra.analysis?.damages?.[dec.damage_index];
        if (!damage) return;
        serverMap[`${ra.id}:${dec.damage_index}`] = {
          chargeType: dec.charge_type === 'tenant' ? 'tenant' : 'wear',
          cost: dec.adjusted_cost != null ? toNumber(dec.adjusted_cost) : baseCost(damage),
          aiCost: baseCost(damage),
          reasonTag: dec.reason_tag || damageProfile(damage).tag,
          dismissed: !!dec.dismissed,
        };
      });
    });
    if (Object.keys(serverMap).length) {
      setDecisionState((prev) => ({ ...serverMap, ...prev }));
    }
  }, [inspection]);

  // Hydrate AI disagreement feedback from server
  useEffect(() => {
    if (!inspection?.room_analyses) return;
    const serverMap = {};
    inspection.room_analyses.forEach((ra) => {
      (ra.feedback || []).forEach((fb) => {
        serverMap[`${ra.id}:${fb.damage_index}`] = fb;
      });
    });
    if (Object.keys(serverMap).length) {
      setFeedbackState((prev) => ({ ...serverMap, ...prev }));
    }
  }, [inspection]);

  // Load persisted inspector notes once per inspection (refetches after an
  // analysis must not clobber in-progress typing).
  useEffect(() => {
    if (!inspection?.id) return;
    if (notesInitRef.current === inspection.id) return;
    notesInitRef.current = inspection.id;
    setInspectorNotes(inspection.notes || '');
  }, [inspection]);

  // Clear any pending debounce timers on unmount.
  useEffect(() => () => {
    Object.values(saveTimersRef.current).forEach(clearTimeout);
    if (notesTimerRef.current) clearTimeout(notesTimerRef.current);
  }, []);

  const trackSave = useCallback(async (request) => {
    pendingSavesRef.current += 1;
    setSaveState('saving');
    try {
      await request();
      pendingSavesRef.current = Math.max(0, pendingSavesRef.current - 1);
      if (pendingSavesRef.current === 0) setSaveState('saved');
    } catch (err) {
      pendingSavesRef.current = Math.max(0, pendingSavesRef.current - 1);
      setSaveState('error');
      toast.error(err.response?.data?.detail || 'Failed to save changes');
    }
  }, []);

  const queueDecisionSave = useCallback((analysisId, damageIndex, decision) => {
    const key = `${analysisId}:${damageIndex}`;
    if (saveTimersRef.current[key]) clearTimeout(saveTimersRef.current[key]);
    saveTimersRef.current[key] = setTimeout(() => {
      delete saveTimersRef.current[key];
      trackSave(() => axios.put(
        `${API}/inspections/${id}/analyses/${analysisId}/decisions/${damageIndex}`,
        {
          charge_type: decision.chargeType === 'tenant' ? 'tenant' : 'wear',
          adjusted_cost: toNumber(decision.cost),
          reason_tag: decision.reasonTag || '',
          dismissed: !!decision.dismissed,
        }
      ));
    }, 400);
  }, [id, trackSave]);

  const saveNotes = useCallback((value) => {
    trackSave(() => axios.patch(`${API}/inspections/${id}`, { notes: value }));
  }, [id, trackSave]);

  const handleNotesChange = (value) => {
    setInspectorNotes(value);
    if (notesTimerRef.current) clearTimeout(notesTimerRef.current);
    notesTimerRef.current = setTimeout(() => {
      notesTimerRef.current = null;
      saveNotes(value);
    }, 800);
  };

  const flushNotes = () => {
    if (notesTimerRef.current) {
      clearTimeout(notesTimerRef.current);
      notesTimerRef.current = null;
      saveNotes(inspectorNotes);
    }
  };

  const handlePhotoFiles = useCallback(async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    const prepared = [];
    for (const file of list) {
      if (file.size > 50 * 1024 * 1024) {
        toast.error(`${file.name || 'File'} too large (max 50MB)`);
        continue;
      }
      const processed = await preprocessPhoto(file);
      const { preview, base64 } = await fileToPreviewData(processed);
      prepared.push({ id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, base64, preview });
    }
    if (!prepared.length) return;
    setPhotos((prev) => {
      const merged = [...prev, ...prepared];
      if (merged.length > MAX_PHOTOS) {
        toast.error(`Maximum ${MAX_PHOTOS} photos per room — extra photos were skipped`);
      }
      return merged.slice(0, MAX_PHOTOS);
    });
  }, []);

  const removePhoto = useCallback((photoId) => {
    setPhotos((prev) => prev.filter((p) => p.id !== photoId));
  }, []);

  const onDrop = useCallback(async (acceptedFiles) => {
    await handlePhotoFiles(acceptedFiles);
  }, [handlePhotoFiles]);

  const handleCameraCapture = async () => {
    try {
      const file = await captureInspectionPhoto();
      if (file) await handlePhotoFiles([file]);
    } catch {
      toast.error('Could not open camera');
    }
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: { 'image/jpeg': [], 'image/png': [], 'image/webp': [] },
    maxFiles: MAX_PHOTOS,
    noClick: isMobileWeb,
    noKeyboard: isMobileWeb,
  });

  const mapJobStage = (job) => {
    if (job.stage) return job.stage;
    if (job.status === 'queued') return 'received';
    if (job.status === 'processing') return 'ai_analyzing';
    if (job.status === 'completed') return 'complete';
    return 'received';
  };

  const submitFeedback = async () => {
    if (!feedbackDialog || !selectedAnalysis?.id) return;
    if (feedbackComment.trim().length < 10) {
      toast.error('Please add at least 10 characters explaining the disagreement');
      return;
    }
    setFeedbackSaving(true);
    try {
      const { idx } = feedbackDialog;
      const res = await axios.put(
        `${API}/inspections/${id}/analyses/${selectedAnalysis.id}/feedback/${idx}`,
        { feedback_type: feedbackType, comment: feedbackComment.trim() }
      );
      const fb = res.data.feedback;
      setFeedbackState((prev) => ({ ...prev, [`${selectedAnalysis.id}:${idx}`]: fb }));
      toast.success('Feedback recorded — thank you');
      setFeedbackDialog(null);
      setFeedbackComment('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save feedback');
    } finally {
      setFeedbackSaving(false);
    }
  };

  const openFeedbackDialog = (idx, damage) => {
    const key = `${selectedAnalysis?.id}:${idx}`;
    const existing = feedbackState[key];
    setFeedbackType(existing?.feedback_type || 'wrong_damage');
    setFeedbackComment(existing?.comment || '');
    setFeedbackDialog({ idx, damage });
  };

  const analyzePhoto = async () => {
    if (!photos.length) { toast.error('Upload at least one photo first'); return; }
    if (!roomName.trim()) { toast.error('Enter a room name'); return; }

    const roomLabel = roomName.trim();
    setAnalyzing(true);
    setAnalyzeStage('received');
    try {
      const res = await axios.post(`${API}/inspections/${id}/analyze`, {
        images_base64: photos.map((p) => p.base64),
        room_name: roomLabel,
        view_label: viewLabel,
      });
      const jobId = res.data.job_id;

      const deadline = Date.now() + 4 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 1500));
        const { data: job } = await axios.get(`${API}/analysis-jobs/${jobId}`);
        const stage = mapJobStage(job);
        setAnalyzeStage(stage);
        if (job.status === 'completed') {
          toast.success(`${roomLabel} analyzed`);
          setPhotos([]);
          setViewLabel('overview');
          fetchInspection();
          // Non-blocking feedback prompt (dismissal remembered per inspection).
          window.dispatchEvent(new CustomEvent('realspect:feedback-prompt', {
            detail: { trigger: 'post_analysis', inspectionId: id },
          }));
          return;
        }
        if (job.status === 'failed') {
          toast.error(job.error || 'Analysis failed. Please try again.');
          return;
        }
      }
      toast.error('Analysis is taking longer than expected — it may still finish. Refresh in a moment.');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Analysis failed');
    } finally {
      setAnalyzing(false);
      setAnalyzeStage('idle');
    }
  };

  const deleteInspection = async () => {
    try {
      await axios.delete(`${API}/inspections/${id}`);
      toast.success('Inspection deleted');
      navigate('/inspections');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete inspection');
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await axios.get(`${API}/inspections/${id}/report/pdf`, { responseType: 'blob' });
      const blob = res.data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inspection-report-${id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (err) {
      // Blob responses carry their error body as a Blob, so go by status.
      if (err.response?.status === 402) openProDialog();
      else toast.error("Couldn't create the PDF. Try again in a moment.");
    }
  };

  const completeInspection = async () => {
    try {
      await axios.post(`${API}/inspections/${id}/complete`);
      toast.success('Inspection approved');
      fetchInspection();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve inspection');
    }
  };

  const isCompleted = inspection?.status === 'completed';
  const roomAnalyses = useMemo(() => inspection?.room_analyses || [], [inspection]);

  // Room sessions group photos of the same room as observations (views)
  // instead of pretending each photo batch is a separate room. Falls back to
  // client-side grouping when talking to an older backend.
  const roomSessions = useMemo(() => {
    const sessions = inspection?.room_sessions;
    if (sessions?.length) return sessions;
    const groups = new Map();
    roomAnalyses.forEach((ra) => {
      const key = (ra.room_name || '').trim().toLowerCase();
      if (!groups.has(key)) {
        groups.set(key, { id: null, room_name: ra.room_name, room_key: key, observations: [] });
      }
      groups.get(key).observations.push({
        analysis_id: ra.id,
        view_label: ra.view_label || 'overview',
        analyzed_at: ra.analyzed_at,
        damage_count: (ra.analysis?.damages || []).length,
      });
    });
    return [...groups.values()];
  }, [inspection, roomAnalyses]);

  const saveLabel =
    saveState === 'saving' ? 'Saving…' :
    saveState === 'saved' ? 'All changes saved' :
    saveState === 'error' ? 'Save failed — retry your last change' : '';
  const saveLabelClass = saveState === 'error' ? 'text-sev-high' : 'text-ink-muted';

  const selectedAnalysis = useMemo(() => {
    if (!roomAnalyses.length) return null;
    return roomAnalyses.find((ra) => ra.id === selectedAnalysisId) || roomAnalyses[0];
  }, [roomAnalyses, selectedAnalysisId]);

  const selectedSession = useMemo(() => {
    if (!selectedAnalysis) return null;
    return roomSessions.find((s) =>
      s.observations.some((o) => o.analysis_id === selectedAnalysis.id)
    ) || null;
  }, [roomSessions, selectedAnalysis]);

  const damages = useMemo(() => selectedAnalysis?.analysis?.damages || [], [selectedAnalysis]);

  const annotatedImages = useMemo(
    () => (selectedAnalysis?.analysis?.annotated_images || []).filter((a) => a?.annotated_image_base64),
    [selectedAnalysis]
  );
  const isMultiPhoto = annotatedImages.length > 1;
  const currentAnnotated = isMultiPhoto
    ? annotatedImages[Math.min(selectedPhotoIdx, annotatedImages.length - 1)]
    : null;
  const displayedImageB64 = currentAnnotated?.annotated_image_base64
    || selectedAnalysis?.analysis?.annotated_image_base64;

  useEffect(() => { setSelectedPhotoIdx(0); }, [selectedAnalysisId]);

  const reviewTrace = selectedAnalysis?.analysis?.analysis_trace || null;
  const reviewCounts = useMemo(() => {
    if (!reviewTrace) return '';
    const parts = [];
    if (reviewTrace.claude_adjusted) parts.push(`${reviewTrace.claude_adjusted} adjusted`);
    if (reviewTrace.claude_rejected) parts.push(`${reviewTrace.claude_rejected} removed`);
    if (reviewTrace.claude_added) parts.push(`${reviewTrace.claude_added} added`);
    return parts.length ? parts.join(', ') : 'all findings confirmed';
  }, [reviewTrace]);

  const jumpToDamagePhoto = useCallback((damage) => {
    if (!isMultiPhoto) return;
    const idx = annotatedImages.findIndex((a) => a.photo_index === (damage?.photo_index || 1));
    if (idx >= 0) setSelectedPhotoIdx(idx);
  }, [isMultiPhoto, annotatedImages]);

  const regionTypes = useMemo(() => {
    const regions = selectedAnalysis?.analysis?.damage_regions || [];
    if (!isMultiPhoto || !currentAnnotated) return regions;
    return regions.filter((r) => (r.photo_index || 1) === currentAnnotated.photo_index);
  }, [selectedAnalysis, isMultiPhoto, currentAnnotated]);

  const getDecision = useCallback((damage, idx) => {
    if (!selectedAnalysis?.id) return null;
    const key = `${selectedAnalysis.id}:${idx}`;
    const existing = decisionState[key];
    if (existing) return existing;

    const initial = {
      chargeType: damage?.is_tenant_chargeable ? 'tenant' : 'wear',
      cost: baseCost(damage),
      aiCost: baseCost(damage),
      reasonTag: damageProfile(damage).tag,
      dismissed: false,
    };
    return initial;
  }, [decisionState, selectedAnalysis]);

  const updateDecision = (idx, patch, damage) => {
    if (!selectedAnalysis?.id || isCompleted) return;
    const key = `${selectedAnalysis.id}:${idx}`;
    const merged = { ...getDecision(damage, idx), ...patch };
    setDecisionState((prev) => ({ ...prev, [key]: merged }));
    queueDecisionSave(selectedAnalysis.id, idx, merged);
  };

  const totals = useMemo(() => {
    let totalRepair = 0;
    let tenantDeduction = 0;
    damages.forEach((d, i) => {
      const entry = getDecision(d, i);
      if (entry?.dismissed) return;
      totalRepair += toNumber(entry?.cost);
      if (entry?.chargeType === 'tenant') tenantDeduction += toNumber(entry.cost);
    });
    const remainingDeposit = Math.max(ASSUMED_DEPOSIT - tenantDeduction, 0);
    return { totalRepair, tenantDeduction, remainingDeposit };
  }, [damages, getDecision]);

  const timeline = useMemo(() => {
    const events = [];
    if (inspection?.created_at) {
      events.push({ label: 'Inspection Created', at: inspection.created_at });
    }
    roomAnalyses.forEach((ra) => {
      if (ra.analyzed_at) events.push({ label: `AI Processed ${ra.room_name}`, at: ra.analyzed_at });
    });
    if (inspection?.completed_at) {
      events.push({ label: 'Inspection Approved', at: inspection.completed_at });
    }
    return events.sort((a, b) => new Date(a.at) - new Date(b.at));
  }, [inspection, roomAnalyses]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-sunken rounded-md w-64" />
          <div className="h-[520px] bg-sunken rounded-md border border-line" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div data-testid="inspection-detail-page" className="space-y-6">
        <Link to="/inspections" className="inline-flex items-center gap-1.5 text-sm font-bold text-ink-muted no-underline hover:text-ink" data-testid="back-to-inspections">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Inspections
        </Link>

        {/* Context header */}
        <div className="flex flex-wrap items-start justify-between gap-6 rounded-md border border-line bg-surface p-6">
          <div className="min-w-0 flex-[1_1_320px]">
            <p className="m-0 text-xs font-bold uppercase leading-4 tracking-[0.08em] text-ink-muted">
              {formatType(inspection?.inspection_type)} inspection
            </p>
            <h1 className="m-0 mt-1 font-display text-3xl font-bold leading-9 tracking-[-0.01em]" data-testid="inspection-title">{inspection?.property_name}</h1>
            <p className="m-0 mt-1 text-ink-muted">{inspection?.property_address}</p>
            <p className="m-0 mt-1.5 font-mono text-sm font-semibold text-ink-muted">
              Started {new Date(inspection?.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              {roomAnalyses.length > 0 && ` · ${roomAnalyses.length} ${roomAnalyses.length === 1 ? 'room view' : 'room views'}`}
            </p>
            {isCompleted && (
              <p className="m-0 mt-3 inline-flex items-center gap-1.5 rounded-sm bg-sunken px-2 py-1 text-[13px] font-bold text-ink">
                <Lock className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" /> Approved and locked
              </p>
            )}
          </div>

          <DamageScore score={inspection?.overall_score} />

          <div className="flex w-full flex-col gap-2 sm:w-auto">
            <Button onClick={gatePro(downloadPdf)} data-testid="export-report-button">
              <FileDown strokeWidth={1.5} />Export report{!hasPro && <ProTag className="ml-1" />}
            </Button>
            {proDialog}
            <Button onClick={share.start} disabled={share.busy} variant="outline" data-testid="share-tenant-button">
              <Share2 strokeWidth={1.5} />Share with tenant{!hasPro && <ProTag className="ml-1" />}
            </Button>
            {share.dialog}
            {shareProDialog}
            {!isCompleted && (
              <Button onClick={completeInspection} variant="outline"><CheckCircle strokeWidth={1.5} />Approve inspection</Button>
            )}
            <Button
              onClick={() => setDeleteConfirmOpen(true)}
              variant="danger"
              data-testid="delete-inspection-button"
            >
              <Trash2 strokeWidth={1.5} />Delete inspection
            </Button>
          </div>
        </div>

        {/* Visual + financial workflow */}
        <div className="grid lg:grid-cols-5 gap-6">
          {/* Left: visual proof engine */}
          <div className="lg:col-span-3 space-y-4">
            <div className="bg-surface border border-line rounded-md p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="m-0 font-display text-[22px] font-bold leading-7">Photo evidence</h2>
                <button
                  type="button"
                  onClick={() => setOverlayOn((v) => !v)}
                  className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-line-strong text-ink hover:bg-sunken"
                >
                  {overlayOn ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />} AI Overlay {overlayOn ? 'On' : 'Off'}
                </button>
              </div>

              <div className="relative rounded-md border border-line bg-sunken min-h-[360px] overflow-hidden flex items-center justify-center">
                {displayedImageB64 ? (
                  <img
                    src={`data:image/png;base64,${displayedImageB64}`}
                    alt={`Damage map for ${selectedAnalysis.room_name}`}
                    className="w-full h-auto max-h-[500px] object-contain"
                    data-testid="annotated-image"
                  />
                ) : (
                  <div className="text-center px-6">
                    <Camera className="w-8 h-8 text-ink-muted mx-auto mb-2" />
                    <p className="text-sm text-ink-muted">No analyzed image for this room yet.</p>
                  </div>
                )}

                {overlayOn && regionTypes.length > 0 && (
                  <div className="absolute left-3 top-3 max-w-[60%] space-y-1.5">
                    {regionTypes.slice(0, 6).map((r, idx) => (
                      <div key={`${r.type}-${idx}`} className="text-xs inline-flex items-center gap-1.5 px-2 py-1 rounded bg-marker text-on-marker mr-1 mb-1">
                        {(r.label ?? idx + 1)}. {formatType(r.type)}
                        {r.severity ? ` · ${r.severity}` : ''}
                        {r.confidence != null ? ` · ${Math.round(toNumber(r.confidence) * 100)}%` : ''}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {isMultiPhoto && (
                <div className="mt-3 flex gap-2 overflow-x-auto pb-1" data-testid="photo-tabs">
                  {annotatedImages.map((a, i) => (
                    <button
                      type="button"
                      key={a.photo_index}
                      onClick={() => setSelectedPhotoIdx(i)}
                      data-testid={`photo-tab-${a.photo_index}`}
                      className={`shrink-0 rounded-md border p-1.5 text-left transition-colors ${
                        currentAnnotated?.photo_index === a.photo_index ? 'border-ink bg-marker-soft': 'border-line bg-surface hover:bg-sunken'
                      }`}
                    >
                      <img
                        src={`data:image/png;base64,${a.annotated_image_base64}`}
                        alt={`Photo ${a.photo_index}`}
                        className="h-14 w-20 object-cover rounded"
                      />
                      <p className={`text-xs font-semibold mt-1 ${a.damage_count ? 'text-sev-med': 'text-sev-low'}`}>
                        Photo {a.photo_index} · {a.damage_count ? `${a.damage_count} issue${a.damage_count > 1 ? 's' : ''}` : 'clear'}
                      </p>
                    </button>
                  ))}
                </div>
              )}

              {/* Rooms with their observations (views), not one tab per photo batch */}
              <div className="mt-3 space-y-2" data-testid="room-session-groups">
                {roomSessions.map((session) => {
                  const containsSelected = session.observations.some((o) => o.analysis_id === selectedAnalysis?.id);
                  return (
                    <div
                      key={session.room_key || session.id}
                      className={`rounded-md border p-2.5 ${containsSelected ? 'border-ink bg-sunken' : 'border-line bg-surface'}`}
                      data-testid={`room-session-${session.room_key}`}
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-ink">{session.room_name}</p>
                        <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full bg-sunken text-ink-muted border border-line">
                          {session.observations.length} view{session.observations.length > 1 ? 's' : ''}
                        </span>
                        {session.photo_count > session.observations.length && (
                          <span className="text-xs text-ink-muted">{session.photo_count} photos</span>
                        )}
                        {session.capture_duration_seconds > 0 && (
                          <span className="text-xs text-ink-muted">
                            {Math.max(1, Math.round(session.capture_duration_seconds / 60))} min capture
                          </span>
                        )}
                      </div>
                      <div className="mt-1.5 flex gap-1.5 overflow-x-auto pb-0.5">
                        {session.observations.map((obs) => (
                          <button
                            type="button"
                            key={obs.analysis_id}
                            onClick={() => setSelectedAnalysisId(obs.analysis_id)}
                            className={`shrink-0 rounded-md border px-2.5 py-1.5 text-left transition-colors ${
                              selectedAnalysis?.id === obs.analysis_id
                                ? 'border-ink bg-surface ring-1 ring-ink'
                                : 'border-line bg-surface hover:bg-sunken'
                            }`}
                            data-testid={`observation-tab-${obs.analysis_id}`}
                          >
                            <p className="text-xs font-semibold text-ink">
                              {VIEW_LABELS_MAP[obs.view_label] || 'Overview'}
                            </p>
                            <p className={`text-xs ${obs.damage_count ? 'text-sev-med': 'text-sev-low'}`}>
                              {obs.damage_count ? `${obs.damage_count} issue${obs.damage_count > 1 ? 's' : ''}` : 'clear'}
                              {obs.analyzed_at ? ` · ${new Date(obs.analyzed_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : ''}
                            </p>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right: financial breakdown */}
          <div className="lg:col-span-2">
            <div className="bg-surface border border-line rounded-md overflow-hidden">
              {!isCompleted && (
                <div className="p-4 border-b border-line">
                  <h2 className="m-0 font-display text-[22px] font-bold leading-7">Add room photos</h2>
                  <p className="m-0 mb-3 mt-0.5 text-sm text-ink-muted">Up to {MAX_PHOTOS} angles per room.</p>
                  <div className="space-y-3">
                    {roomOptions.length > 0 && (
                      <Select
                        value={roomMode === 'custom' ? '__custom__' : (roomName || undefined)}
                        onValueChange={(v) => {
                          if (v === '__custom__') {
                            setRoomMode('custom');
                            setRoomName('');
                          } else {
                            setRoomMode('list');
                            setRoomName(v);
                          }
                        }}
                      >
                        <SelectTrigger data-testid="room-select" className="bg-sunken border-line text-ink h-10">
                          <SelectValue placeholder="Select room" />
                        </SelectTrigger>
                        <SelectContent className="bg-surface border-line">
                          {roomOptions.map((name) => (
                            <SelectItem key={name} value={name}>{name}</SelectItem>
                          ))}
                          <SelectItem value="__custom__">Other room…</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    {(roomMode === 'custom' || !roomOptions.length) && (
                      <Input
                        value={roomName}
                        onChange={(e) => setRoomName(e.target.value)}
                        placeholder="Room name (e.g. Master Bedroom)"
                        className="bg-sunken border-line text-ink placeholder:text-ink-muted h-10"
                      />
                    )}
                    <Select value={viewLabel} onValueChange={setViewLabel}>
                      <SelectTrigger data-testid="view-label-select" className="bg-sunken border-line text-ink h-10">
                        <SelectValue placeholder="What does this photo show?" />
                      </SelectTrigger>
                      <SelectContent className="bg-surface border-line">
                        {VIEW_OPTIONS.map((v) => (
                          <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {isMobileWeb ? (
                      <div className="space-y-2">
                        <input
                          id="mobile-camera-input"
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          capture="environment"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (file) await handlePhotoFiles([file]);
                            e.target.value = '';
                          }}
                        />
                        <Button
                          type="button"
                          onClick={() => document.getElementById('mobile-camera-input')?.click()}
                          disabled={photos.length >= MAX_PHOTOS}
                          className="w-full h-12 bg-marker hover:bg-marker-hover text-on-marker"
                          data-testid="mobile-take-photo-button"
                        >
                          <Camera className="w-5 h-5 mr-2" />
                          {photos.length ? `Add Photo (${photos.length}/${MAX_PHOTOS})` : 'Take Photo'}
                        </Button>
                        <Button type="button" variant="outline" onClick={open} disabled={photos.length >= MAX_PHOTOS} className="w-full h-10 border-line-strong text-ink">
                          Choose from library
                        </Button>
                        <div {...getRootProps()} className="hidden">
                          <input {...getInputProps()} />
                        </div>
                      </div>
                    ) : (
                      <div
                        {...getRootProps()}
                        data-testid="photo-dropzone"
                        className={`border-2 border-dashed rounded-md p-4 text-center cursor-pointer transition-colors ${
                          isDragActive ? 'border-ink bg-marker': 'border-line-strong hover:border-line-strong'
                        }`}
                      >
                        <input {...getInputProps()} />
                        {photos.length ? (
                          <div className="grid grid-cols-3 gap-2" onClick={(e) => e.stopPropagation()}>
                            {photos.map((p, i) => (
                              <div key={p.id} className="relative">
                                <img src={p.preview} alt={`Photo ${i + 1}`} className="h-16 w-full object-cover rounded" />
                                <button
                                  onClick={() => removePhoto(p.id)}
                                  className="absolute -top-1.5 -right-1.5 bg-surface border border-line rounded-full p-0.5 text-ink-muted hover:text-sev-high"
                                  type="button"
                                  data-testid={`remove-photo-${i}`}
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ))}
                            {photos.length < MAX_PHOTOS && (
                              <button
                                type="button"
                                onClick={open}
                                data-testid="add-more-photos-button"
                                className="h-16 rounded border border-dashed border-line-strong text-xs text-ink-muted hover:border-ink hover:text-ink"
                              >
                                + Add
                              </button>
                            )}
                          </div>
                        ) : (
                          <>
                            <Upload className="w-6 h-6 text-ink-muted mx-auto mb-1" />
                            <p className="text-xs text-ink-muted">Drop or click to upload up to {MAX_PHOTOS} photos</p>
                          </>
                        )}
                      </div>
                    )}
                    {photos.length > 0 && isMobileWeb && (
                      <div className="grid grid-cols-3 gap-2">
                        {photos.map((p, i) => (
                          <div key={p.id} className="relative rounded-md border border-line overflow-hidden">
                            <img src={p.preview} alt={`Photo ${i + 1}`} className="h-20 w-full object-cover" />
                            <button
                              type="button"
                              onClick={() => removePhoto(p.id)}
                              className="absolute top-1 right-1 bg-surface rounded-full p-0.5 text-ink-muted"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {isNativeApp() && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleCameraCapture}
                        className="w-full h-10 border-line-strong"
                      >
                        <Camera className="w-4 h-4 mr-2" />
                        Take Photo
                      </Button>
                    )}
                    {analyzing && (
                      <div className="rounded-md border border-line bg-sunken p-3 space-y-2" role="status" aria-live="polite">
                        <p className="m-0 text-xs font-bold uppercase tracking-[0.08em] text-ink-muted">Analysis progress</p>
                        {ANALYZE_STEPS.map((step) => {
                          const order = ANALYZE_STEPS.findIndex((s) => s.key === analyzeStage);
                          const stepOrder = ANALYZE_STEPS.findIndex((s) => s.key === step.key);
                          const done = stepOrder < order || analyzeStage === 'complete';
                          const active = step.key === analyzeStage;
                          return (
                            <div key={step.key} className={`flex items-center gap-2 text-xs ${active ? 'text-ink font-semibold': done ? 'text-sev-low': 'text-ink-muted'}`}>
                              {done ? <CheckCircle className="w-3.5 h-3.5" /> : active ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Clock className="w-3.5 h-3.5" />}
                              {step.label}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <Button
                      onClick={analyzePhoto}
                      disabled={analyzing || !photos.length || !roomName.trim()}
                      className="w-full bg-marker hover:bg-marker-hover text-on-marker h-10"
                      data-testid="analyze-room-button"
                    >
                      {analyzing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      {analyzing ? 'Analyzing…' : `Analyze Room${photos.length > 1 ? ` (${photos.length} photos)` : ''}`}
                    </Button>
                  </div>
                </div>
              )}

              <div className="p-4 border-b border-line">
                <div className="flex items-center justify-between gap-2">
                  <h2 className="m-0 font-display text-[22px] font-bold leading-7">Charges</h2>
                  {saveLabel && <span className={`text-xs ${saveLabelClass}`} data-testid="decision-save-state">{saveLabel}</span>}
                </div>
                <p className="text-sm text-ink-muted mt-1">
                  Current view: {selectedAnalysis?.room_name || 'N/A'}
                  {isCompleted && <span className="text-sev-low"> — approved &amp; locked</span>}
                </p>
                {reviewTrace?.claude_agreement != null && (
                  <div
                    className={`mt-2 flex items-start gap-2 rounded-md border p-2.5 text-xs ${
                      reviewTrace.claude_agreement >= 85
                        ? 'border-sev-low/40 bg-sev-low-soft text-ink'
                        : reviewTrace.claude_agreement >= 70
                          ? 'border-sev-med/40 bg-sev-med-soft text-ink'
                          : 'border-sev-high/40 bg-sev-high-soft text-ink'
                    }`}
                    data-testid="claude-review-banner"
                  >
                    <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
                    <div>
                      <p className="m-0 font-bold">
                        AI cross-review: {reviewTrace.claude_agreement}% agreement
                      </p>
                      <p className="m-0 mt-0.5 text-ink-muted">
                        Claude independently re-examined every finding
                        {reviewCounts ? ` — ${reviewCounts}` : ''}.
                        {reviewTrace.claude_agreement < 70 && ' Inspector review recommended.'}
                      </p>
                    </div>
                  </div>
                )}
                {reviewTrace?.single_model_mode && (
                  <div className="mt-2 flex items-start gap-2 rounded-md border border-sev-med/40 bg-sev-med-soft p-2.5 text-xs" data-testid="single-model-banner">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <p className="m-0">
                      <span className="font-bold">Single-model analysis.</span>{' '}
                      The cross-review model was unavailable for this run — inspector review recommended.
                    </p>
                  </div>
                )}
              </div>

              <div className="max-h-[560px] overflow-y-auto p-4 space-y-3">
                {!damages.length && (
                  <p className="text-sm text-ink-muted">No damage items in the selected room analysis.</p>
                )}

                {damages.map((damage, idx) => {
                  const profile = damageProfile(damage);
                  const decision = getDecision(damage, idx);
                  const rowKey = `${selectedAnalysis?.id}:${idx}`;
                  const isEditing = editingCostKey === rowKey;

                  if (decision?.dismissed) {
                    return (
                      <div key={rowKey} className="border border-line rounded-md p-3 bg-surface opacity-75">
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <p className="text-sm font-semibold text-ink-muted line-through">{profile.title}</p>
                            <p className="text-xs text-ink-muted mt-0.5">Dismissed — excluded from totals and report</p>
                          </div>
                          {!isCompleted && (
                            <button
                              type="button"
                              onClick={() => updateDecision(idx, { dismissed: false }, damage)}
                              className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-line-strong text-ink-muted hover:bg-sunken shrink-0"
                            >
                              <Undo2 className="w-3.5 h-3.5" /> Restore
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  }

                  const edited = toNumber(decision?.cost) !== toNumber(decision?.aiCost);
                  const choice = (active) => `h-9 rounded-md border text-sm font-bold transition-colors disabled:cursor-not-allowed ${
                    active ? 'border-ink bg-ink text-paper' : 'border-line-strong bg-surface text-ink hover:bg-sunken disabled:text-ink-muted'
                  }`;
                  return (
                    <div key={rowKey} className="rounded-md border border-line bg-surface p-4" data-testid={`finding-row-${idx}`}>
                      <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-x-3 gap-y-1.5">
                        <EvidenceMarker n={idx + 1} className="mt-0.5" />
                        <div className="min-w-0">
                          <p className="m-0 font-bold">{profile.title}</p>
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {damage.severity && <SeverityChip severity={damage.severity} />}
                            <ProvenanceChip source={edited ? 'ai_edited' : 'ai'} />
                            {damage.claude_verdict === 'confirm' && (
                              <span className="inline-flex h-6 items-center gap-1 rounded-sm bg-sev-low-soft px-2 text-[11px] font-bold text-sev-low" title="Independently verified by a second AI model (Claude)" data-testid={`claude-verdict-${idx}`}>
                                <ShieldCheck className="w-3 h-3" /> Cross-checked
                              </span>
                            )}
                            {damage.claude_verdict === 'adjust' && (
                              <span className="inline-flex h-6 items-center gap-1 rounded-sm bg-sev-med-soft px-2 text-[11px] font-bold text-sev-med" title={damage.review_note || 'Severity or cost corrected during the AI cross-review'} data-testid={`claude-verdict-${idx}`}>
                                <ShieldCheck className="w-3 h-3" /> Adjusted by review
                              </span>
                            )}
                            {(damage.claude_verdict === 'added' || damage.found_by === 'claude_review') && (
                              <span className="inline-flex h-6 items-center gap-1 rounded-sm bg-sunken px-2 text-[11px] font-bold text-ink" title="Found by the reviewing model; not localized on the photo" data-testid={`claude-verdict-${idx}`}>
                                <ShieldCheck className="w-3 h-3" /> Added by review
                              </span>
                            )}
                            {isMultiPhoto && (
                              <button
                                type="button"
                                onClick={() => jumpToDamagePhoto(damage)}
                                className="inline-flex h-6 items-center rounded-sm bg-sunken px-2 font-mono text-[13px] font-semibold text-ink hover:bg-line"
                                title="Show the photo where this damage was found"
                                data-testid={`damage-photo-badge-${idx}`}
                              >
                                Photo {damage.photo_index || 1}
                              </button>
                            )}
                          </div>
                        </div>
                        {!isCompleted && (
                          <button
                            type="button"
                            onClick={() => updateDecision(idx, { dismissed: true }, damage)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-ink-muted hover:bg-sev-high-soft hover:text-sev-high"
                            aria-label={`Dismiss ${profile.title}`}
                            title="Dismiss this finding (left out of totals and the report)"
                          >
                            <Trash2 className="h-5 w-5" strokeWidth={1.5} />
                          </button>
                        )}
                        <p className="col-start-2 col-end-4 m-0 text-sm leading-[22px] text-ink-muted">{profile.description}</p>
                        {damage.review_note && (
                          <p className="col-start-2 col-end-4 m-0 mt-1 text-xs italic leading-[18px] text-ink-muted" data-testid={`review-note-${idx}`}>
                            Reviewer: {damage.review_note}
                          </p>
                        )}
                      </div>

                      <div className="mt-4 grid grid-cols-2 gap-2" role="group" aria-label="Who pays for this">
                        <button
                          type="button"
                          disabled={isCompleted}
                          aria-pressed={decision?.chargeType === 'wear'}
                          onClick={() => updateDecision(idx, { chargeType: 'wear' }, damage)}
                          className={choice(decision?.chargeType === 'wear')}
                        >
                          Normal wear
                        </button>
                        <button
                          type="button"
                          disabled={isCompleted}
                          aria-pressed={decision?.chargeType === 'tenant'}
                          onClick={() => updateDecision(idx, { chargeType: 'tenant' }, damage)}
                          className={choice(decision?.chargeType === 'tenant')}
                        >
                          Tenant charge
                        </button>
                      </div>

                      <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
                        <div>
                          <p className="m-0 text-xs font-bold uppercase leading-4 tracking-[0.08em] text-ink-muted">Estimated cost</p>
                          {isEditing && !isCompleted ? (
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={decision?.cost ?? 0}
                              onChange={(e) => updateDecision(idx, { cost: toNumber(e.target.value) }, damage)}
                              onBlur={() => setEditingCostKey(null)}
                              aria-label={`Estimated cost for ${profile.title}`}
                              className="mt-1 h-10 w-36 rounded-md border border-line-strong bg-surface px-2.5 font-mono text-base font-bold text-ink"
                              autoFocus
                            />
                          ) : (
                            <button
                              type="button"
                              disabled={isCompleted}
                              onClick={() => setEditingCostKey(rowKey)}
                              className={`mt-1 border-b-2 border-dashed border-line-strong font-mono text-[22px] font-bold leading-7 tabular hover:border-ink disabled:cursor-not-allowed disabled:border-transparent ${
                                decision?.chargeType === 'wear' ? 'text-ink-muted line-through' : 'text-ink'
                              }`}
                              aria-label={`Estimated cost ${money(decision?.cost)}. Edit`}
                            >
                              {money(decision?.cost)}
                            </button>
                          )}
                          {edited && (
                            <p className="m-0 mt-1 font-mono text-sm font-semibold text-ink-muted">AI estimate {money(decision?.aiCost)}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {feedbackState[`${selectedAnalysis?.id}:${idx}`] && (
                            <StatusChip done>Correction sent</StatusChip>
                          )}
                          <Button type="button" variant="ghost" size="sm" onClick={() => openFeedbackDialog(idx, damage)}>
                            <MessageSquare strokeWidth={1.5} />Correct this
                          </Button>
                        </div>
                      </div>

                      {decision?.chargeType === 'tenant' && (
                        <div className="mt-4">
                          <p className="m-0 mb-2 text-xs font-bold uppercase leading-4 tracking-[0.08em] text-ink-muted">Reason</p>
                          <div className="flex flex-wrap gap-1.5">
                            {QUICK_TAGS.map((tag) => (
                              <button
                                type="button"
                                key={tag}
                                disabled={isCompleted}
                                aria-pressed={decision?.reasonTag === tag}
                                onClick={() => updateDecision(idx, { reasonTag: tag }, damage)}
                                className={`h-8 rounded-sm border px-2.5 text-[13px] font-bold disabled:cursor-not-allowed ${
                                  decision?.reasonTag === tag ? 'border-ink bg-ink text-paper' : 'border-line-strong bg-surface text-ink hover:bg-sunken'
                                }`}
                              >
                                {tag}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="sticky bottom-0 border-t border-line bg-surface p-4">
                <div className="grid grid-cols-3 gap-px overflow-hidden rounded-md border border-line bg-line">
                  <div className="bg-surface p-2.5">
                    <p className="m-0 text-xs font-bold uppercase leading-4 tracking-[0.08em] text-ink-muted">Total repair</p>
                    <p className="m-0 mt-0.5 font-mono text-lg font-bold tabular">{money(totals.totalRepair)}</p>
                  </div>
                  <div className="bg-surface p-2.5">
                    <p className="m-0 text-xs font-bold uppercase leading-4 tracking-[0.08em] text-ink-muted">Tenant charges</p>
                    <p className="m-0 mt-0.5 font-mono text-lg font-bold tabular">{money(totals.tenantDeduction)}</p>
                  </div>
                  <div className="bg-surface p-2.5">
                    <p className="m-0 text-xs font-bold uppercase leading-4 tracking-[0.08em] text-ink-muted">Deposit left</p>
                    <p className="m-0 mt-0.5 font-mono text-lg font-bold tabular">{money(totals.remainingDeposit)}</p>
                  </div>
                </div>
                <p className="m-0 mt-2 text-[13px] leading-[18px] text-ink-muted">Deposit left assumes a {money(ASSUMED_DEPOSIT)} deposit.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Room evidence: 2D coverage, duplicate review, manual findings */}
        {selectedSession && (
          <RoomEvidencePanel
            inspectionId={id}
            session={selectedSession}
            isCompleted={isCompleted}
            onSelectView={(viewId) => {
              setViewLabel(viewId);
              if (selectedSession?.room_name) setRoomName(selectedSession.room_name);
            }}
          />
        )}

        {/* Audit trail */}
        <div className="bg-surface border border-line rounded-md p-5">
          <div className="grid lg:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center justify-between gap-2">
                <h3 className="m-0 inline-flex items-center gap-2 font-display text-[22px] font-bold leading-7">
                  <NotebookText className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" /> Inspector notes
                </h3>
                {saveLabel && <span className={`text-xs ${saveLabelClass}`}>{saveLabel}</span>}
              </div>
              <textarea
                value={inspectorNotes}
                onChange={(e) => handleNotesChange(e.target.value)}
                onBlur={flushNotes}
                placeholder="Add objective context that AI may have missed (location specifics, vendor observations, manual exceptions). Saved automatically and included in the PDF report."
                aria-label="Inspector notes"
                className="mt-3 w-full min-h-[130px] rounded-md border border-line-strong bg-surface p-3 text-base leading-6 text-ink placeholder:text-ink-muted"
              />
            </div>

            <div>
              <h3 className="m-0 inline-flex items-center gap-2 font-display text-[22px] font-bold leading-7">
                <Clock className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" /> Timeline
              </h3>
              <div className="mt-3 rounded-md border border-line bg-sunken p-3 max-h-[170px] overflow-y-auto">
                {timeline.length === 0 && <p className="text-sm text-ink-muted">No timeline events yet.</p>}
                <ul className="space-y-2">
                  {timeline.map((event, idx) => (
                    <li key={`${event.label}-${idx}`} className="text-sm text-ink flex items-start justify-between gap-3">
                      <span>{event.label}</span>
                      <span className="text-xs text-ink-muted whitespace-nowrap">{new Date(event.at).toLocaleString()}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={!!feedbackDialog} onOpenChange={(open) => !open && setFeedbackDialog(null)}>
        <DialogContent className="bg-surface border-line sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-ink">Disagree with AI?</DialogTitle>
            <DialogDescription className="text-ink-muted">
              Help us improve — your feedback is saved separately from billing decisions.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-2">
              <Label className="text-ink">What&apos;s wrong?</Label>
              <Select value={feedbackType} onValueChange={setFeedbackType}>
                <SelectTrigger className="bg-sunken border-line">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-surface">
                  {FEEDBACK_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-ink">Explain (min 10 characters)</Label>
              <Textarea
                value={feedbackComment}
                onChange={(e) => setFeedbackComment(e.target.value)}
                placeholder="e.g. This is normal wear from the previous tenant, not new damage."
                className="bg-sunken border-line min-h-[100px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFeedbackDialog(null)} className="border-line-strong">
              Cancel
            </Button>
            <Button onClick={submitFeedback} disabled={feedbackSaving} className="bg-marker hover:bg-marker-hover text-on-marker">
              {feedbackSaving ? 'Saving…' : 'Submit feedback'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent className="bg-surface border-line text-ink">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-ink">Delete Inspection?</AlertDialogTitle>
            <AlertDialogDescription className="text-ink-muted">
              This will permanently delete this inspection and all associated room analyses. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-line-strong text-ink hover:bg-sunken">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteInspection} className="bg-sev-high hover:bg-sev-high text-surface border-0">
              Delete Inspection
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
